import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Box,
  Alert,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGraphEditorStore } from '@/graph-store';
import { getMergeCandidates } from '@/graph/domain/topology';
import { useToast } from '@/hooks/useToast';
import type { MaterialNodeSpec } from '@/graph/domain/types';

export interface MergeMaterialModalProps {
  /** ID of the material being merged (source endpoint) */
  sourceMaterialId: string;
  /** Direction context: ROOT or LEAF for target filtering */
  contextType: 'ROOT' | 'LEAF';
  onClose: () => void;
  onMerged?: (targetMaterialId: string) => void;
}

/**
 * Modal for selecting a target material to merge into.
 *
 * Shows candidates filtered by:
 * - Different connected component
 * - Opposite endpoint type (merge requires combining complementary endpoints)
 * - Not a reminder node
 * - Not in a group
 *
 * Displays merge direction:
 * - ROOT clicked: "clicked node will merge INTO selected target"
 * - LEAF clicked: "selected target will merge INTO clicked node"
 */
export function MergeMaterialModal({
  sourceMaterialId,
  contextType,
  onClose,
  onMerged,
}: MergeMaterialModalProps) {
  const graphData = useGraphEditorStore((s) => s.graphData);
  const mergeMaterials = useGraphEditorStore(
    (s) => (s as any).mergeMaterials,
  );
  const [selectedTargetId, setSelectedTargetId] = React.useState<string>('');
  const { showError } = useToast();

  // Get source material info
  const sourceMaterial = graphData.materialNodes[sourceMaterialId];

  // Get merge candidates
  const candidates = useMemo(() => {
    return getMergeCandidates(sourceMaterialId, contextType, graphData);
  }, [sourceMaterialId, contextType, graphData]);

  const handleConfirm = async () => {
    if (!selectedTargetId) return;

    try {
      if (mergeMaterials) {
        await mergeMaterials(sourceMaterialId, selectedTargetId, contextType);
        onMerged?.(selectedTargetId);
      }
      onClose();
    } catch (error) {
      // Show error toast with appropriate message based on error code
      let errorMessage = 'Failed to merge materials';

      if (error instanceof Error) {
        if (error.message.includes('SAME_MATERIAL')) {
          errorMessage = 'Cannot merge a material with itself';
        } else if (error.message.includes('SAME_COMPONENT')) {
          errorMessage = 'Materials must be in different branches';
        } else if (error.message.includes('INVALID_ENDPOINTS')) {
          errorMessage = 'Both materials must be endpoints (start/end)';
        } else if (error.message.includes('SOURCE_NOT_FOUND')) {
          errorMessage = 'Source material not found';
        } else if (error.message.includes('TARGET_NOT_FOUND')) {
          errorMessage = 'Target material not found';
        } else if (error.message.includes('BOTH_GROUPED')) {
          errorMessage = 'Cannot merge: materials are in groups';
        } else if (error.message.includes('GROUP_MEMBER_MERGE')) {
          errorMessage = 'Cannot merge group members';
        } else {
          errorMessage = error.message || errorMessage;
        }
      }

      showError(errorMessage);
      console.error('Failed to merge materials:', error);
    }
  };

  if (!sourceMaterial) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 1.5,
          }}
        >
          Merge Material
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="error">Source material not found</Alert>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', px: 3, py: 2 }}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1.5,
        }}
      >
        Merge Material
        <IconButton size="small" onClick={onClose} sx={{ ml: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            Source Material
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {sourceMaterial.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {contextType === 'ROOT' ? 'Root (no incoming edges)' : 'Leaf (no outgoing edges)'}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            Merge Direction
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {contextType === 'ROOT'
              ? 'Clicked node will merge INTO selected target'
              : 'Selected target will merge INTO clicked node'}
          </Typography>
        </Box>

        {candidates.length === 0 ? (
          <Alert severity="info">
            No available materials to merge. Ensure there are disconnected branches with
            complementary endpoint types.
          </Alert>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              Choose a node
            </Typography>
            <FormControl sx={{ minWidth: 220 }} size="small">
              <Select
                id="target-select"
                displayEmpty
                value={selectedTargetId}
                onChange={(e) => setSelectedTargetId(e.target.value)}
              >
                <MenuItem value="" disabled>
                  Select
                </MenuItem>
                {candidates.map((material) => (
                  <MenuItem key={material.id} value={material.id}>
                    {material.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="outlined"
          disabled={!selectedTargetId || candidates.length === 0}
        >
          Merge
        </Button>
      </DialogActions>
    </Dialog>
  );
}